/**
 * BlogCta.tsx — Call-to-action component for blog posts
 * Shown on non-welcome blog posts to drive conversions (extension install + app signup)
 *
 * Copy is keyed by the POST'S language (posts are single-language SEO pages),
 * not the visitor locale — a ko post shows ko CTA copy regardless of who reads it.
 * source param follows the post language too (blog_ko etc.) for attribution.
 *
 * Cluster-aware (landing-v12 P0): exam/list/method clusters reach readers who
 * arrived with their OWN material ("수능 영단어", "영어 단어장") — for them the
 * app IS the product (paste a list → FSRS cards, no install). Those clusters
 * get app-first CTAs; capture/tool clusters keep extension-first.
 */

import { CWS_URL } from '../landing/ExtensionPromo'
import { track } from '../../lib/betterstack'

/** Clusters whose readers convert on "bring your own material" — the web app
 *  entry, not the extension. Everything else keeps extension-first. */
const APP_FIRST = new Set(['exam-vocab', 'vocab-notebook', 'srs-method', 'card-making'])

const COPY: Record<string, { title: string; sub: string; subApp: string; install: string; open: string }> = {
  en: {
    title: 'Try Piccard for free',
    sub: '150 free energy to start. No credit card required.',
    subApp: 'Bring your word list — paste the text or drop the PDF, and it becomes FSRS-scheduled cards. 150 free energy to start, no credit card.',
    install: 'Install Extension',
    open: 'Open App',
  },
  ko: {
    title: 'Piccard 무료로 시작하기',
    sub: '처음 150 에너지는 무료예요. 신용카드가 필요 없어요.',
    subApp: '갖고 있는 단어 목록을 붙여넣거나 PDF를 올리면 FSRS 복습 카드가 됩니다. 처음 150 에너지는 무료예요.',
    install: '확장 프로그램 설치',
    open: '앱 열기',
  },
  ja: {
    title: 'Piccard を無料で始める',
    sub: '最初の150エネルギーは無料。クレジットカードは不要です。',
    subApp: '単語リストを貼り付けるかPDFをアップロードすると、FSRS復習のカードになります。最初の150エネルギーは無料です。',
    install: '拡張機能をインストール',
    open: 'アプリを開く',
  },
  zh: {
    title: '免费开始用 Piccard',
    sub: '首 150 能量免费，无需信用卡。',
    subApp: '把词表粘贴进来或上传 PDF,就成了 FSRS 复习卡。首 150 能量免费,无需信用卡。',
    install: '安装扩展程序',
    open: '打开应用',
  },
  'zh-TW': {
    title: '免費開始用 Piccard',
    sub: '首 150 能量免費，無需信用卡。',
    subApp: '把字表貼上或上傳 PDF,就成了 FSRS 複習卡。首 150 能量免費，無需信用卡。',
    install: '安裝擴充功能',
    open: '開啟應用程式',
  },
}

interface BlogCtaProps {
  lang: string // post language: 'en', 'ko', 'ja', 'zh', 'zh-TW'
  cluster?: string // registry cluster — decides app-first vs extension-first
}

export function BlogCta({ lang, cluster }: BlogCtaProps) {
  const copy = COPY[lang] ?? COPY.en
  const sourceParam = `source=blog_${lang}`
  const appFirst = cluster !== undefined && APP_FIRST.has(cluster)
  const sub = appFirst ? copy.subApp : copy.sub

  const appLink = (
    <a
      href={`/?${sourceParam}`}
      onClick={() => track('blog-cta-click', { mode: appFirst ? 'app-first' : 'extension-first', cluster, lang })}
      className={`rounded-full px-5 py-2.5 font-medium text-center transition-colors ${
        appFirst
          ? 'bg-primary text-on-primary shadow-card hover:opacity-95 transition-opacity'
          : 'border-2 border-primary text-primary hover:bg-primary/5'
      }`}
    >
      {copy.open}
    </a>
  )
  const installLink = (
    <a
      href={CWS_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => track('blog-cta-click', { mode: appFirst ? 'app-first' : 'extension-first', cluster, lang })}
      className={`rounded-full px-5 py-2.5 font-medium text-center transition-colors ${
        appFirst
          ? 'border-2 border-primary text-primary hover:bg-primary/5'
          : 'bg-primary text-on-primary shadow-card hover:opacity-95 transition-opacity'
      }`}
    >
      {copy.install}
    </a>
  )

  return (
    <div className="my-8 flex flex-col gap-3 rounded-lg border border-outline-variant bg-surface-variant p-6 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex-1">
        <h3 className="font-headline font-semibold text-lg text-on-surface mb-1">{copy.title}</h3>
        <p className="text-sm text-muted-soft">{sub}</p>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        {appFirst ? (
          <>
            {appLink}
            {installLink}
          </>
        ) : (
          <>
            {installLink}
            {appLink}
          </>
        )}
      </div>
    </div>
  )
}
